import React, { useEffect, useState, useCallback, useMemo } from "react";
import style from "../caterpilar-plague/caterpilar-plague.module.css";
import CaterPillarIamge from "../../../../images/caterpillar-colored.png";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ParkOutlinedIcon from "@mui/icons-material/ParkOutlined";
import logger from "../../../../utils/logger";
import BracellRowExtras from "./BracellRowExtras";
import BracellLevelPanel from "./BracellLevelPanel";
import { diagnoseBracellCaterpillar } from "../../config/bracellRegistryProfile";

function createRow(index, plantCount) {
  const pc = Math.max(1, plantCount);
  return {
    planta: (index % pc) + 1,
    parcela: Math.floor(index / pc) + 1,
    numeroFolhas: 100,
    numeroLagartas: 0,
    desfolha_incidencia_percent: "0",
    dano_severidade: "Sem Dano",
  };
}

function groupByParcela(samples, plantCount) {
  const pc = Math.max(1, plantCount);
  const groups = [];
  for (let i = 0; i < samples.length; i += pc) {
    groups.push(samples.slice(i, i + pc));
  }
  return groups;
}

function withPercentualDesfolha(row) {
  const nf = parseInt(row.numeroFolhas, 10) || 0;
  const nl = parseInt(row.numeroLagartas, 10) || 0;
  const pct = nf > 0 ? Math.round((nl / nf) * 100) : 0;
  return { ...row, percentualDesfolha: String(pct) };
}

function BracellCaterpillarPlague({ plague, infestation, data, plantCount, parcelas: parcelasProp }) {
  const [samples, setSamples] = useState([]);
  const isValidPlague =
    plague && typeof plague === "object" && plague.name && plague.scientific;

  const numParcelas = Math.max(1, Number(parcelasProp) || 2);
  const numPlantCount = Math.max(1, Number(plantCount) || 5);

  useEffect(() => {
    const total = numParcelas * numPlantCount;
    setSamples(Array.from({ length: total }, (_, i) => createRow(i, numPlantCount)));
  }, [numParcelas, numPlantCount]);

  const patchRow = useCallback((index, patch) => {
    setSamples((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...patch };
      return next;
    });
  }, []);

  const handleChangeNumeroFolhas = (index, value) => {
    const v = parseInt(value, 10);
    patchRow(index, { numeroFolhas: Number.isFinite(v) && v >= 0 ? v : 0 });
  };

  const handleChangeNumeroLagartas = (index, value) => {
    const v = parseInt(value, 10);
    patchRow(index, { numeroLagartas: Number.isFinite(v) && v >= 0 ? v : 0 });
  };

  const addLag = (index, delta) => {
    setSamples((prev) => {
      const next = [...prev];
      const cur = parseInt(next[index].numeroLagartas, 10) || 0;
      next[index] = {
        ...next[index],
        numeroLagartas: Math.max(0, cur + delta),
      };
      return next;
    });
  };

  const payloadRows = useMemo(
    () => samples.map(withPercentualDesfolha),
    [samples]
  );

  useEffect(() => {
    if (!data || !isValidPlague) return;
    data(payloadRows);
  }, [payloadRows, data, isValidPlague]);

  const { lines, displayText, levelKey, diagnostic, prob } = useMemo(() => {
    const groups = groupByParcela(samples, numPlantCount);
    const parcelaRatios = groups.map((group) => {
      const totalF = group.reduce((acc, s) => acc + (parseInt(s.numeroFolhas, 10) || 0), 0);
      const totalL = group.reduce((acc, s) => acc + (parseInt(s.numeroLagartas, 10) || 0), 0);
      return totalF > 0 ? (totalL / totalF) * 100 : 0;
    });
    const mean = parcelaRatios.length > 0
      ? parcelaRatios.reduce((a, b) => a + b, 0) / parcelaRatios.length
      : 0;

    const totalL = samples.reduce((acc, s) => acc + (parseInt(s.numeroLagartas, 10) || 0), 0);
    const totalF = samples.reduce((acc, s) => acc + (parseInt(s.numeroFolhas, 10) || 0), 0);

    const d = diagnoseBracellCaterpillar(mean);
    const disp = d.levelKey === "none" ? "-" : d.diagnostic;

    const ls = [
      { label: "Total de lagartas", value: totalL },
      { label: "Total de folhas", value: totalF },
    ];
    if (numParcelas > 1) {
      parcelaRatios.forEach((r, p) => {
        ls.push({ label: `Parcela ${p + 1} — % Desfolha`, value: `${r.toFixed(1)}%` });
      });
      ls.push({ label: "Média geral de Desfolha", value: `${mean.toFixed(1)}%` });
    } else {
      ls.push({ label: "% desfolha (global)", value: `${mean.toFixed(1)}%` });
    }
    return {
      lines: ls,
      displayText: disp,
      levelKey: d.levelKey,
      diagnostic: d.diagnostic,
      prob: d.prob,
    };
  }, [samples, numPlantCount, numParcelas]);

  useEffect(() => {
    if (!isValidPlague) return;
    infestation({ diagnostic, prob });
  }, [diagnostic, prob, infestation, isValidPlague]);

  if (!isValidPlague) {
    logger.warn("Objeto de praga inválido:", plague);
    return null;
  }

  return (
    <div>
      <div className={style.card}>
        <div className={style["sample-plague"]}>
          <img src={CaterPillarIamge} alt="" />
          <div className={style["name-sample-plague"]}>
            <div>{plague.name}</div>
            <div>{plague.scientific}</div>
          </div>
        </div>

        <div className={style.samples}>
          {samples.map((sample, index) => {
            const parcelaStart = index % numPlantCount === 0;
            const parcelaNum = Math.floor(index / numPlantCount) + 1;
            return (
              <React.Fragment key={index}>
                {numParcelas > 1 && parcelaStart && (
                  <div style={{ fontWeight: "600", fontSize: "0.85rem", padding: "10px 0 4px", color: "#40a8c4" }}>
                    Parcela {parcelaNum}
                  </div>
                )}
                <div className={style["collected-sample-title"]}>
                  <div className={style["collected-sample-plant"]}>
                    <div className={style["collected-sample-plant-number"]}>
                      {sample.planta}° <ParkOutlinedIcon style={{ width: "0.8rem" }} />
                    </div>
                  </div>
                  <div className={style["div-number-caterpillar"]}>
                    <div className={style["collected-sample-plant-number-caterpillar"]}>
                      N° de lagartas
                    </div>
                    <div className={style["container-button"]}>
                      <RemoveIcon
                        onClick={() => addLag(index, -1)}
                        className={style.icon}
                      />
                      <input
                        className={style["collected-sample-plant-number-caterpillar-input"]}
                        value={sample.numeroLagartas}
                        onChange={(e) => handleChangeNumeroLagartas(index, e.target.value)}
                        min={0}
                        type="number"
                      />
                      <AddIcon
                        onClick={() => addLag(index, 1)}
                        className={style.icon}
                      />
                    </div>
                  </div>
                  <div className={style.separations}>
                    <div className={style["collected-sample-plant-caterpillar"]}>
                      <div className={style["collected-sample-plant-number-sheet"]}>
                        N° de folhas
                      </div>
                      <input
                        className={style["collected-sample-plant-number-sheet-input"]}
                        value={sample.numeroFolhas}
                        onChange={(e) => handleChangeNumeroFolhas(index, e.target.value)}
                        type="number"
                      />
                    </div>
                    <div className={style["collected-sample-plant-number-percentual"]}>
                      <p>Percentual de desfolha</p>
                      <div className={style.percentual}>
                        <p>
                          {withPercentualDesfolha(sample).percentualDesfolha}%
                        </p>
                      </div>
                    </div>
                  </div>
                  <BracellRowExtras
                    desfolha_incidencia_percent={sample.desfolha_incidencia_percent}
                    dano_severidade={sample.dano_severidade}
                    onPatch={(p) => patchRow(index, p)}
                  />
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <BracellLevelPanel
        lines={lines}
        displayText={displayText}
        levelKey={levelKey}
      />
    </div>
  );
}

export default BracellCaterpillarPlague;
